import Logger from '@utils/log'
import { memo, useCallback } from 'react'
import { useWire } from '@forminator/react-wire'
import {
    realtimeUserPresenceCount,
    realtimeOrganizationPresenceCount,
    notifications as storeNotifications,
} from '@store'
import {
    REALTIME_CHANNEL_TYPE_USERS,
    REALTIME_CHANNEL_TYPE_ORGANIZATIONS,
    RT_EVENT_TYPE_UPDATE_NOTIFICATION,
    RT_EVENT_TYPE_DISMISS_NOTIFICATION,
    RT_EVENT_TYPE_REFRESH_PROFILE,
} from '@constants/realtime'
import RealtimeSubscription from '@components/shared/Realtime/RealtimeSubscription'
import deepmerge from 'deepmerge'
import { addNotificationsToStore, fetchProfile } from '@actions'

const log = new Logger('RealtimeNotifications')

/**
 * Container for all real-time subscriptions
 * 
 * @returns {JSX.Element}
 * @constructor
 */
const RealtimeNotifications = () => {
    
    const userPresenceCount = useWire(realtimeUserPresenceCount)
    const organizationPresenceCount = useWire(realtimeOrganizationPresenceCount)
    const notifications = useWire(storeNotifications)
    
    const onUserPresence = presenceData =>
        userPresenceCount.setValue(presenceData.length)
    
    const onOrganizationPresence = presenceData =>
        organizationPresenceCount.setValue(presenceData.length)
    
    const addNotification = useCallback((channelName, message) => addNotificationsToStore({
        channelName,
        message,
    }), [])
    
    //region Incoming Message Handlers
    
    const handleRefreshProfile = async () => {
        
        try {
            await fetchProfile()
            log.d('handleRefreshProfile', 'profile refreshed')
        } catch (e) {
            log.e('handleRefreshProfile', 'failed to refresh profile', e)
        }
        
    }
    
    const handleUpdateNotification = message => {
        
        const prev = notifications.getValue()
        
        notifications.setValue(prev.map(it => {
            
            if (it.message.data.id === message.data.data.notificationId)
                it.message.data.data = deepmerge(it.message.data.data, message.data)
            
            return it
            
        }))
        
    }
    
    const handleDismissNotification = message => {
        
        notifications.setValue(notifications.getValue()
            .filter(it => it.message.data.id !== message.data.data.notificationId))
        
    }
    
    //endregion Incoming Message Handlers
    
    // @todo: maybe we can use this instead of the actions on src/actions/notifications.js
    const handleMessage = async (channelName, message) => {
        
        log.d('handleMessage', channelName, message)
        
        switch (message.data.type) {
            
            case RT_EVENT_TYPE_REFRESH_PROFILE:
                return await handleRefreshProfile()
            
            case RT_EVENT_TYPE_UPDATE_NOTIFICATION:
                return handleUpdateNotification(message)
            
            case RT_EVENT_TYPE_DISMISS_NOTIFICATION:
                return handleDismissNotification(message)
            
            default:
                addNotification(channelName, message)
            
        }
        
    }
    
    return (<>
        
        <RealtimeSubscription
            channelType={REALTIME_CHANNEL_TYPE_USERS}
            onMessage={handleMessage}
            onPresence={onUserPresence} />
        
        <RealtimeSubscription
            channelType={REALTIME_CHANNEL_TYPE_ORGANIZATIONS}
            onMessage={handleMessage}
            onPresence={onOrganizationPresence} />
    
    </>)
    
}

export default memo(RealtimeNotifications)
